/**
 * @file     RealtorQuestion.tsx
 * @purpose  "Are you working with a REALTOR®?" Yes / No, required on every
 *           listing request that could imply agency (showing, open house,
 *           offer, documents). Yes adds one line that the visitor's REALTOR®
 *           can also do this for them; the request still sends.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { gateColors } from '@/components/gate/GateSheet';
import { SheetLabel } from '@/components/lead-capture/ListingSheet';

type Answer = '' | 'yes' | 'no';

export interface RealtorQuestionProps {
  value: boolean | null;
  onChange: (value: boolean) => void;
  /** The Yes line, worded for the form ("The REALTOR® you work with can also book this for you."). */
  yesLine: string;
  /** Submit was tried with no answer. */
  error?: boolean;
  disabled?: boolean;
  idPrefix: string;
}

export function RealtorQuestion({ value, onChange, yesLine, error = false, disabled = false, idPrefix }: RealtorQuestionProps) {
  const { t } = useTranslation();
  const labelId = `${idPrefix}-realtor-label`;
  const errorId = `${idPrefix}-realtor-error`;
  const label = t('forms.realtor.question', { defaultValue: 'Are you working with a REALTOR®?' });
  const answer: Answer = value === true ? 'yes' : value === false ? 'no' : '';

  return (
    <Box data-realtor-question sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SheetLabel id={labelId}>{label}</SheetLabel>
      <SegmentedControl<Answer>
        options={[
          { value: 'yes', label: t('forms.realtor.yes', { defaultValue: 'Yes' }), disabled },
          { value: 'no', label: t('forms.realtor.no', { defaultValue: 'No' }), disabled },
        ]}
        value={answer}
        onChange={(next) => onChange(next === 'yes')}
        ariaLabel={label}
        labelledBy={labelId}
        describedBy={error && value === null ? errorId : undefined}
        required
        invalid={error && value === null}
        fullWidth
        id={`${idPrefix}-realtor`}
      />
      {error && value === null ? (
        <Typography id={errorId} component="p" role="alert" sx={{ m: 0, fontSize: 13.5, lineHeight: 1.45, color: gateColors.error }}>
          {t('forms.realtor.required', { defaultValue: 'Choose Yes or No.' })}
        </Typography>
      ) : null}
      {value === true ? (
        <Typography component="p" data-realtor-yes sx={{ m: 0, fontSize: 14, lineHeight: 1.45, color: gateColors.textSecondary }}>
          {yesLine}
        </Typography>
      ) : null}
    </Box>
  );
}
